/**
 * Importe. Es un decimal con el simbolo de la moneda a la vista.
 *
 * El simbolo va DENTRO del control, no en la etiqueta: en una liquidacion con
 * doce importes, saber cual es en pesos y cual en dolares no puede depender de
 * leer el titulo del campo. La moneda sale de `options.currency` de la metadata.
 */

import { controlClass } from '@/components/fields/controls';
import { ReadonlyOutput } from '@/components/fields/FieldDisplay';
import { LOCALE } from '@/components/fields/format';
import { fraccionDe, useNumericBuffer } from '@/components/fields/numeric';
import type { FieldInputProps } from '@/components/fields/types';
import type { FieldMeta } from '@/lib/types';

/** Codigo de moneda declarado en la metadata, o ARS por defecto. */
function monedaDe(meta: FieldMeta): string {
  const opciones = meta.options ?? {};
  return typeof opciones.currency === 'string' ? opciones.currency : 'ARS';
}

/** Simbolo de la moneda declarada, o el codigo si el navegador no lo conoce. */
function simbolo(meta: FieldMeta): string {
  const moneda = monedaDe(meta);
  try {
    const partes = new Intl.NumberFormat(LOCALE, { style: 'currency', currency: moneda }).formatToParts(0);
    return partes.find((parte) => parte.type === 'currency')?.value ?? moneda;
  } catch {
    return moneda;
  }
}

/** Cuántos decimales usa la unidad menor de la moneda (ARS/USD = 2). */
function unidadesMenores(moneda: string): number {
  try {
    return (
      new Intl.NumberFormat(LOCALE, { style: 'currency', currency: moneda }).resolvedOptions()
        .maximumFractionDigits ?? 2
    );
  } catch {
    return 2;
  }
}

export function MonetaryField({
  id,
  meta,
  value,
  onChange,
  readonly,
  required,
  invalid,
  describedBy,
}: FieldInputProps) {
  // Un importe muestra SIEMPRE los centavos de la moneda (piso = unidad menor,
  // 2 para el peso) y hasta la precision que declara el campo. Asi un precio con
  // 4 decimales cargado en redondo se ve "1.150.000,00" —no "1.150.000,0000"—
  // pero si trae decimales reales ("…,1234") no se pierden. El techo nunca baja
  // del piso, para no truncar los centavos de una moneda de baja precision.
  const menores = unidadesMenores(monedaDe(meta));
  const precision = fraccionDe(meta);
  const buffer = useNumericBuffer(value, onChange, {
    entero: false,
    decimal: meta.type !== 'float',
    minFraccion: Math.min(menores, precision),
    maxFraccion: Math.max(menores, precision),
  });

  if (readonly) {
    return <ReadonlyOutput id={id} meta={meta} value={value} {...(describedBy === undefined ? {} : { describedBy })} />;
  }

  return (
    <div className="relative">
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-fg-faint"
      >
        {simbolo(meta)}
      </span>
      <input
        id={id}
        type="text"
        inputMode="decimal"
        className={controlClass(invalid, 'pl-10 text-right tabular-nums')}
        value={buffer.texto}
        required={required}
        aria-required={required || undefined}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        autoComplete="off"
        onFocus={buffer.alEntrar}
        onChange={(evento) => {
          buffer.alEscribir(evento.target.value);
        }}
        onBlur={buffer.alSalir}
      />
    </div>
  );
}
